/**
 * Copyright (c) 2026 Zoldier & LCN Softwares All rights reserved.
 *
 * This software is the confidential and proprietary information of Zoldier & LCN Software.
 * You shall not disclose such confidential information and shall use it only
 * in accordance with the terms of the license agreement you entered into with
 * Zoldier & LCN Software.
 */

import type { SupportedLocale } from '../locales';

/**
 * SEO meta tag descriptor consumed by the `MetaTags` component.
 *
 * All fields are readonly to prevent accidental mutation of the default
 * SEO configuration at runtime. Each field maps directly to a specific
 * HTML meta element or link relation rendered by `MetaTags`.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export interface SeoMeta {
  readonly title: string;
  readonly description: string;
  readonly canonicalUrl: string;
  readonly ogImage: string;
  readonly locale: SupportedLocale;
}

/**
 * Default SEO values for the landing page, per locale.
 *
 * These are used when the page does not provide its own SEO overrides.
 * Override per-route by passing custom values to the `MetaTags` component.
 *
 * Values are keyed by `SupportedLocale` so a lookup by the active locale
 * always yields a fully-typed `SeoMeta` object with no undefined fields.
 * All canonical and OG URLs point to the production domain `ziaplatforms.com`.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export const DEFAULT_SEO: Record<SupportedLocale, SeoMeta> = {
  en: {
    title: 'ZIA Platforms — Embed AI inside your enterprise applications',
    description:
      'The enterprise AI infrastructure that lets organizations embed intelligent assistants into their applications — without data ever leaving the client infrastructure.',
    canonicalUrl: 'https://ziaplatforms.com/',
    ogImage: 'https://ziaplatforms.com/og-image.png',
    locale: 'en',
  },
  es: {
    title: 'ZIA Platforms — IA empresarial dentro de tus aplicaciones',
    description:
      'La infraestructura de IA empresarial que permite a las organizaciones integrar asistentes inteligentes en sus aplicaciones — sin que los datos salgan de la infraestructura del cliente.',
    canonicalUrl: 'https://ziaplatforms.com/',
    ogImage: 'https://ziaplatforms.com/og-image.png',
    locale: 'es',
  },
};
